import { Object as FabricObject } from 'fabric/fabric-impl';
import { MediaManagerTemplateFilter } from 'modules/core/domain/interfaces/supabase/media-manager/actions';
import {
  MediaManagerRenderedImage,
  MediaManagerTemplate,
} from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { MatchLayoutSettings } from 'modules/manager/domain/match-layout';
import supabase from 'modules/core/supabase/supabase';

export interface UpdateTemplateObjectsInput {
  id: number;
  objects: FabricObject[];
  version: string;
}

export interface UpdateTemplateMatchLayoutInput {
  id: number;
  matchLayout: MatchLayoutSettings;
}

export interface PersistRenderedImageInput {
  matchId: number;
  templateId: number;
  dataUrl: string;
}

export class MediaManagerRepository {
  getTemplates(filter?: MediaManagerTemplateFilter): Promise<MediaManagerTemplate[]> {
    return supabase.mediaManager.getTemplates(filter);
  }

  getTemplateById(id: number): Promise<MediaManagerTemplate> {
    return supabase.mediaManager.getTemplateById(id);
  }

  updateTemplateObjects(input: UpdateTemplateObjectsInput): Promise<MediaManagerTemplate> {
    return supabase.mediaManager.updateTemplate({
      id: input.id,
      objects: input.objects,
      version: input.version,
    });
  }

  updateTemplateMatchLayout(input: UpdateTemplateMatchLayoutInput): Promise<MediaManagerTemplate> {
    return supabase.mediaManager.updateTemplate({
      id: input.id,
      matchLayout: input.matchLayout,
    });
  }

  async persistRenderedImage(input: PersistRenderedImageInput): Promise<MediaManagerRenderedImage> {
    const previous = await supabase.mediaManagerRenderedImages.getByMatchAndTemplate(
      input.matchId,
      input.templateId,
    );
    const previousImageUrl = previous?.imageUrl ?? null;

    const { publicUrl } = await supabase.renderedImagesStorage.upload(
      input.matchId,
      input.templateId,
      input.dataUrl,
    );

    let result: MediaManagerRenderedImage;
    try {
      result = await supabase.mediaManagerRenderedImages.upsert({
        matchId: input.matchId,
        templateId: input.templateId,
        imageUrl: publicUrl,
      });
    } catch (err) {
      await supabase.renderedImagesStorage.remove(publicUrl).catch((cleanupErr) => {
        console.error('[render] storage rollback failed', cleanupErr);
      });
      throw err;
    }

    if (previousImageUrl && previousImageUrl !== publicUrl) {
      await supabase.renderedImagesStorage.remove(previousImageUrl).catch((cleanupErr) => {
        console.error('[render] previous image cleanup failed', cleanupErr);
      });
    }

    return result;
  }

  async deleteRenderedImage(id: string): Promise<void> {
    const row = await supabase.mediaManagerRenderedImages.getById(id);
    if (!row) return;

    await supabase.renderedImagesStorage.remove(row.imageUrl);
    await supabase.mediaManagerRenderedImages.deleteById(id);
  }
}

const mediaManagerRepository = new MediaManagerRepository();
export default mediaManagerRepository;
